import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { formatMoney } from "@/lib/billing";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const { t, lang } = useLang();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("polling"); // polling | paid | timeout
  const [billing, setBilling] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("timeout"); return; }
    let active = true;
    const poll = async () => {
      if (!active) return;
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await refresh();
          try { setBilling((await api.get("/payments/me")).data); } catch (error) { console.error(error); }
          setStatus("paid");
          return;
        }
      } catch (error) {
        console.error("Payment status polling failed:", error);
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
  }, [params, refresh]);

  return (
    <div className="min-h-screen grid place-items-center bg-background px-6">
      <div className="chenia-card p-10 text-center max-w-md w-full">
        {status === "polling" && (<>
          <Loader2 className="h-12 w-12 text-primary animate-spin mx-auto" />
          <h1 className="mt-5 font-serif text-2xl font-semibold">{t("processing_payment")}</h1>
        </>)}
        {status !== "polling" && (<>
          <CheckCircle2 className="h-14 w-14 text-chenia-sage mx-auto" />
          <h1 className="mt-5 font-serif text-3xl font-semibold" data-testid="payment-success-title">{t("payment_success")}</h1>
          <p className="mt-2 text-muted-foreground">{t("payment_success_desc")}</p>
          {billing?.amount != null && billing?.currency && (
            <p className="mt-2 text-sm" data-testid="payment-success-amount">
              {t(`plan_${billing.tier}`)} · {formatMoney(billing.amount, billing.currency, lang)} {t("billing_per_month")}
            </p>
          )}
          <Button onClick={() => nav("/dashboard")} className="mt-6 rounded-full bg-primary hover:bg-chenia-terracottadark" data-testid="payment-back-dashboard">
            {t("back_to_dashboard")}
          </Button>
        </>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const { t } = useLang();
  const nav = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center bg-background px-6">
      <div className="chenia-card p-10 text-center max-w-md w-full">
        <XCircle className="h-14 w-14 text-muted-foreground mx-auto" />
        <h1 className="mt-5 font-serif text-3xl font-semibold">{t("payment_cancel")}</h1>
        <p className="mt-2 text-muted-foreground">{t("payment_cancel_desc")}</p>
        <div className="mt-6 flex gap-3 justify-center">
          <Button variant="outline" onClick={() => nav("/pricing")} className="rounded-full">{t("pricing")}</Button>
          <Button onClick={() => nav("/dashboard")} className="rounded-full bg-primary hover:bg-chenia-terracottadark">{t("back_to_dashboard")}</Button>
        </div>
      </div>
    </div>
  );
}
